import { useId } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';

import { ErrorText } from '../../components/ErrorText';
import { Input } from '../../components/Input';
import { InputGroup } from '../../components/InputGroup';
import { Cell, ErrorList, Host, HostCell, Wrapper } from './HostedCardFields.styles';
import type {
  HostedCardField,
  HostedCardFieldsLabels,
  HostedCardFieldsProps,
} from './HostedCardFields.types';

const DEFAULT_LABELS: Required<HostedCardFieldsLabels> = {
  cardNumber: 'Card number',
  expiry: 'Expiry date',
  expiryPlaceholder: 'Expiry MM/YY',
  securityCode: 'Security code',
};

const FIELD_ORDER: HostedCardField[] = ['cardNumber', 'expiry', 'securityCode'];

const EXPIRY_SEPARATOR = /[\s/.-]+/;

const formatExpiryDigits = (raw: string): string => {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 6) digits = `${digits.slice(0, 2)}${digits.slice(4)}`;
  if (/^[2-9]/.test(digits)) digits = `0${digits}`;
  digits = digits.slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
};

export const formatExpiry = (raw: string): string => {
  const [monthPart, yearPart] = raw.trim().split(EXPIRY_SEPARATOR);
  if (yearPart === undefined) return formatExpiryDigits(raw);
  const month = monthPart.replace(/\D/g, '').slice(0, 2);
  const yearDigits = yearPart.replace(/\D/g, '');
  const isTypingFourDigitYear = yearDigits.length === 3 && yearDigits.startsWith('20');
  const year =
    yearDigits.length >= 4
      ? yearDigits.slice(2, 4)
      : yearDigits.slice(0, isTypingFourDigitYear ? 3 : 2);
  const shownMonth = /^[1-9]$/.test(month) ? `0${month}` : month;
  return year ? `${shownMonth} / ${year}` : shownMonth;
};

export function HostedCardFields({
  cardNumberHostId,
  securityCodeHostId,
  expiryValue,
  onExpiryChange,
  focusedField = null,
  invalidFields = [],
  error,
  disabled = false,
  labels,
}: HostedCardFieldsProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };
  const errorId = useId();
  const invalidNamesId = useId();
  const isInvalid = (field: HostedCardField) => invalidFields.includes(field);
  const fieldLabels: Record<HostedCardField, string> = {
    cardNumber: merged.cardNumber,
    expiry: merged.expiry,
    securityCode: merged.securityCode,
  };
  const invalidNames = FIELD_ORDER.filter(isInvalid).map((field) => fieldLabels[field]);
  const hasError = Boolean(error) || invalidNames.length > 0;
  const describedBy = (field: HostedCardField) =>
    isInvalid(field)
      ? [error ? errorId : null, invalidNamesId].filter(Boolean).join(' ')
      : undefined;

  const handleExpiryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.target;
    const isEditingBeforeEnd = selectionStart !== null && selectionStart < value.length;
    onExpiryChange(isEditingBeforeEnd ? value : formatExpiry(value));
  };

  const handleExpiryBlur = (event: FocusEvent<HTMLInputElement>) => {
    const formatted = formatExpiry(event.target.value);
    if (formatted !== event.target.value) onExpiryChange(formatted);
  };

  return (
    <Wrapper>
      <InputGroup>
        <InputGroup.Row>
          <HostCell
            role="group"
            aria-label={merged.cardNumber}
            aria-describedby={describedBy('cardNumber')}
            aria-disabled={disabled || undefined}
            $focused={focusedField === 'cardNumber'}
            $invalid={isInvalid('cardNumber')}
            $disabled={disabled}
          >
            <Host id={cardNumberHostId} />
          </HostCell>
        </InputGroup.Row>
        <InputGroup.Row>
          <Cell $focused={false} $invalid={isInvalid('expiry')} $disabled={disabled}>
            <Input
              variant="borderless"
              type="text"
              inputMode="numeric"
              autoComplete="cc-exp"
              aria-label={merged.expiry}
              aria-describedby={describedBy('expiry')}
              placeholder={merged.expiryPlaceholder}
              value={expiryValue}
              onChange={handleExpiryChange}
              onBlur={handleExpiryBlur}
              disabled={disabled}
              error={isInvalid('expiry')}
            />
          </Cell>
          <HostCell
            role="group"
            aria-label={merged.securityCode}
            aria-describedby={describedBy('securityCode')}
            aria-disabled={disabled || undefined}
            $focused={focusedField === 'securityCode'}
            $invalid={isInvalid('securityCode')}
            $disabled={disabled}
          >
            <Host id={securityCodeHostId} />
          </HostCell>
        </InputGroup.Row>
      </InputGroup>
      {hasError && (
        <ErrorList>
          {error && (
            <ErrorText id={errorId} role="alert">
              {error}
            </ErrorText>
          )}
          {invalidNames.length > 0 && (
            <ErrorText id={invalidNamesId}>{invalidNames.join(', ')}</ErrorText>
          )}
        </ErrorList>
      )}
    </Wrapper>
  );
}
